import "firebase/functions";

import { appConfig, isPrototypeRevision, makeDropdown, pad } from "../../../utils";
import {
    collection,
    countUpdates,
    getVariantByParentType,
    parentTypeFor,
    readCount,
    refs,
    superRefs
} from "../_variants/index.js";
import {
    documentStoragePaths,
    revisionQuality
} from "./documentUtils";

import firebase from "firebase/app";
import { formIsUsable } from "../../../components/DynamicForm/formToggle.js";
import { reactive } from "vue";

export const documentCheckLoadingFlags = reactive({});

const refDataPoints = ["Number", "ID", "Title", "Date", "Revision", "Parent", "ParentID", "SubDocumentType", "MPDS", "Cancelled"];

// Re-export the canonical implementation that lives with DesignChanges'
// post-create flow. Kept here for back-compat with `Repairs`,
// `FlightConditions` and any other module that still imports from
// `../Documents/actions`.
export { createBasicDocuments } from "../DesignChanges/postActions.js";

const fields = ({ pageItem, meta }) => {
    return [{
        "label": "Title",
        "name": "Title",
        "type": "text",
        "divClass": "md:col-span-3"
    }, {
        "label": "Is Prototype?",
        "name": "Prototype",
        "type": "dropdown",
        "dropdownData": [{
            "label": "Yes",
            "id": "Yes"
        }, {
            "label": "No",
            "id": "No"
        }],
        show({ liveInputData }) {
            return liveInputData.Type === "ACC";
        }
    }, {
        "label": "Sub Document Type",
        "name": "SubDocumentType",
        "type": "dropdown",
        "labelKey": "Name",
        "idKey": "Name",
        "dataLocation": "Database/SubDocumentTypes",
        show({ liveInputData }) {
            return liveInputData.Type === "TREP";
        }
    }, {
        "label": "Manufacturer",
        "name": "Manufacturer",
        "type": "dropdown",
        "labelKey": "Name",
        "idKey": "Name",
        "dataLocation": "Database/Manufacturers",
        show({ liveInputData }) {
            return ["EAF", "EADT"].includes(liveInputData.Type);
        }
    }, {
        "label": "Does this have a MPDS Supplement attached?",
        "name": "MPDS",
        "type": "dropdown",
        "dropdownData": [{
            "label": "Yes",
            "id": true
        }, {
            "label": "No",
            "id": false
        }],
        show({ liveInputData }) {
            return ['TEC', 'AMMS'].includes(liveInputData.Type);
        }
    }];
};

export const addDocument = ({ form, pageItem, meta, store }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");
    const docCollectionTail = docCollection.split("/")[1]
    const docRefs = refs(variant, "Documents");
    const docSuperRefs = superRefs(variant, "Documents");

    form.setItem(pageItem);

    let title = pageItem?.Title.includes(" - ") ? pageItem.Title.split(" - ")[1] : pageItem.Title;
    // if the to has subDocType then we need to update the title

    if (pageItem.SubDocumentType) {
        title = `${pageItem.SubDocumentType} - ${title}`;
    }

    form.setValues({
        "Title": title
    });

    form.setConfig({
        "title": "Add Document",
        "type": "push",
        "location": docCollection,
        "fields": [{
            "label": "Parent",
            "type": "value",
            value() {
                return pageItem.Number
            }
        }, {
            "label": "New Document Number",
            "type": "value",
            value({ liveInputData, dropdownData }) {
                if (!liveInputData.Type) return "Select document type"
                const selectedDocumentType = dropdownData.Type.find(documentType => {
                    return documentType.id === liveInputData.Type
                });
                const count = readCount(selectedDocumentType.meta, variant, "Count");
                return `${variant.docPrefix}${selectedDocumentType.meta.NameShort}-${pad(count)}`
            }
        }, {
            "label": "Type",
            "name": "Type",
            "type": "dropdown",
            "labelKey": "NameShort",
            "idKey": "NameShort",
            "dataLocation": "Database/DocumentTypes",
            dataFilter(documentType) {
                return documentType[meta.ParentType] === true || documentType[meta.ParentType] === "true";
            }
        }, ...fields({ form, pageItem, meta, store })],
        preSubmitHook({ liveInputData, dropdownData }) {
            const selectedDocumentType = dropdownData.Type.find(documentType => {
                return documentType.id === liveInputData.Type
            });

            const startingRevision = selectedDocumentType.meta.NameShort === "FAC" ? "NONE" : liveInputData.Prototype === "Yes" ? "P1" : "1";
            const count = readCount(selectedDocumentType.meta, variant, "Count");

            return {
                "Cancelled": false,
                "Aircraft": pageItem.Aircraft,
                "CreatedBy": store.getters.userName,
                "ExpectedTime": selectedDocumentType.meta.ExpectedTime,
                "Date": {
                    "Created": new Date().toISOString(),
                    "Cancelled": "N/A",
                    "Signedoff": "N/A"
                },
                "SignedOff": false,
                "Links": {
                    [pageItem.ID]: "---R1"
                },
                "Parent": pageItem.Number,
                "ParentID": pageItem.ID,
                "ParentType": meta.ParentType,
                [meta.ParentType]: {
                    [pageItem.ID]: true
                },
                "Revision": startingRevision,
                "RevisionID": "---R1",
                "Revisions": {
                    "---R1": {
                        "Revision": startingRevision,
                        "Reason": "Document Created",
                        "ID": "---R1",
                        "Date": new Date().toISOString(),
                        "Quality": revisionQuality(meta.ParentType === "Documents")
                    }
                },
                "TypeID": selectedDocumentType.meta.ID,
                "UploadStatus": selectedDocumentType.meta.RequiresUploadStatus ? "Not Uploaded" : "N/A",
                "Number": `${variant.docPrefix}${selectedDocumentType.meta.NameShort}-${pad(count)}`
            }
        },
        async postSubmitHook({ db, data, newData, typeRef, dropdownData, liveInputData }) {
            const selectedDocumentType = dropdownData.Type.find(documentType => {
                return documentType.id === liveInputData.Type
            });

            await db.utils.updateRefs(newData, `${docRefs}/`, refDataPoints);
            await db.set(`${docSuperRefs}/${newData.ID}`, {
                k: newData.ID,
                c: newData.Number,
            });

            await db.set(`Database/${meta.ParentType}/${pageItem.ID}/${docCollectionTail}/${typeRef.key}`, true);
            await db.set(`${docCollection}/${typeRef.key}/${meta.ParentType}/${pageItem.ID}`, true);

            const currentCount = readCount(selectedDocumentType.meta, variant, "Count");
            const docTypeUpdates = countUpdates(
                `Database/DocumentTypes/${selectedDocumentType.meta.ID}`,
                variant,
                "Count",
                currentCount + 1
            );
            await Promise.all(Object.entries(docTypeUpdates).map(([loc, val]) => db.set(loc, val)));
        }
    });
    form.getDropdownData();
    form.toggle();
};

// This function edits a document based on the input fields of a given form
export const editDocument = ({ form, item, meta, pageItem, store }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");
    const docRefs = refs(variant, "Documents");
    const docSuperRefs = superRefs(variant, "Documents");

    // Set the values of the form to the existing document's values
    form.setValues(item);

    // Set configuration options for the form
    form.setConfig({
        "title": "Update Document",
        "type": "update",
        "location": `${docCollection}/${item.ID}`,
        "description": `Editing document: ${item.Number}`,
        "fields": fields({ form, pageItem, meta, store }),

        // Async hook that uploads files associated with the edited document
        async postSubmitHook({ data, db, typeRef, files }) {
            let d = {
                ...item,
                ...data,
                "ID": item.ID
            };

            await db.utils.updateRefs(d, docRefs, refDataPoints);
            await db.set(`${docSuperRefs}/${item.ID}`, {
                k: item.ID,
                c: item.Number,
            });

            if (!files || files.length < 1) return;
            for (const file of files) {

                // Upload file to specific directory in database
                await db.upload(`${docCollection}/${item.ID}/Files/${typeRef.key}`, file[0]);
            }
        }
    });

    // Get data for any dropdown components within the form
    form.getDropdownData();

    // Toggle the visibility of the form
    form.toggle();
};

export const cancelDocument = ({ form, item, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");
    const docRefs = refs(variant, "Documents");

    form.setValues(item);
    form.setConfig({
        "title": "Cancel Document",
        "description": `Are you sure you want to cancel document ${item.Number}?`,
        "submitButtonLabel": "Yes, Cancel Document",
        "submitButtonClass": "cancel",
        "type": "update",
        "location": `${docCollection}/${item.ID}`,
        preSubmitHook() {
            return {
                "Cancelled": true
            }
        },
        async postSubmitHook({ db }) {
            await db.set(`${docRefs}/${item.ID}/Cancelled`, true);
        }
    });
    form.toggle();
};

export const checkDocument = type => ({ form, item, meta, pageItem, store, db }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");

    let currentRevision = item.Revisions[item.RevisionID];

    form.setValues(item);
    form.setConfig({
        "title": "Signoff Document",
        "type": "update",
        "description": currentRevision.Quality[type].RolledBack ?
            "This check has been rolled back in the past. Only enter time for work completed since the last roll back." :
            "",
        "descriptionStyles": "bg-orange rounded font-bold m-2",
        "location": `${docCollection}/${item.ID}`,
        "submitButtonLabel": "I confirm",
        "fields": [{
            "label": `I confirm this document complies with the relevant standards outlined in the <a class="font-bold text-primary cursor-pointer" href="${store.state.handbook}" target="_blank">Current Procedure (click to open)</a>.`,
            "name": "ProcedureConfirmation",
            "type": "checkbox",
            "divClass": "md:col-span-3"
        }],
        preSubmitHook({ data }) {
            return {
                [type]: true,
                [`Revisions/${item.RevisionID}/Quality/${type}/By`]: store.getters.userName,
                [`Revisions/${item.RevisionID}/Quality/${type}/Completed`]: true,
                [`Revisions/${item.RevisionID}/Quality/${type}/Date`]: (new Date()).toISOString()
            };
        }
    });
    if (!formIsUsable(form)) return;
    form.toggle();
};

export const uncheckDocument = type => ({ form, item, meta, pageItem, store, db }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");

    form.setValues(item);
    form.setConfig({
        "title": `Cancel ${type} Check`,
        "type": "update",
        "location": `${docCollection}/${item.ID}`,
        "submitButtonLabel": "I confirm",
        "fields": [],
        preSubmitHook({ data }) {
            return {
                [type]: true,
                [`Revisions/${item.RevisionID}/Quality/${type}/By`]: "N/A",
                [`Revisions/${item.RevisionID}/Quality/${type}/Completed`]: false,
                [`Revisions/${item.RevisionID}/Quality/${type}/Date`]: "N/A",
                [`Revisions/${item.RevisionID}/Quality/${type}/RolledBack`]: true

            };
        },
        postSubmitHook({ data }) {
            db.push(`${docCollection}/${item.ID}/Time`, {
                "UserID": store.getters.userID,
                "Date": (new Date()).toISOString(),
                "Type": type,
                "Time": data._time
            });
        }
    });
    form.toggle();
};

export const reviseDocument = ({ form, pageItem, rowItem, meta, store }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");

    // Calculate the next revision
    let nextRevision = isPrototypeRevision(rowItem.Revision) ? `P${parseInt(rowItem.Revision.split("P")[1]) + 1}` : parseInt(rowItem.Revision) + 1;
    let nextRevisionID = `--R${nextRevision}`;
    form.setItem(rowItem);
    form.setConfig({
        "title": "Revise Document",
        "description": `Revise Document <strong class="text-primary">${rowItem.Number}</strong> from Revision <strong class="text-primary">${rowItem.Revision}</strong> to Revision <strong class="text-primary">${nextRevision}</strong>. This action will revise the document and only link the new revision to the current item.<br />Make sure all time has been recorded for this revision.`,
        "location": `${docCollection}/${rowItem.ID}`,
        "type": "update",
        "fields": [{
            "label": "Reason",
            "name": `Revisions[${nextRevisionID}][Reason]`,
            "type": "dropdown",
            "labelKey": "Name",
            "idKey": "Name",
            "dataLocation": "Database/DocumentRevisionTypes",
            "divClass": "cols-span-3"
        }],
        preSubmitHook({ data }) {
            return {
                "Revision": nextRevision,
                "RevisionID": nextRevisionID,
                "Signedoff": false,
                "Completed": false,
                "Revisions": {
                    [nextRevisionID]: {
                        "Date": (new Date()).toISOString(),
                        "Revision": nextRevision,
                        "RevisedBy": store.state.user.Basic.name,
                        "ID": nextRevisionID,
                        "Quality": revisionQuality(rowItem.ParentType === "Documents")
                    }
                },
                "Links": {
                    [pageItem.ID]: nextRevisionID
                }
            }
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const revisionRollback = ({ form, item, db, meta, pageItem, toast }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");

    //order the revisions by date from oldest to newest
    let revisions = Object.values(item.Revisions).sort((a, b) => {
        return new Date(a.Date) - new Date(b.Date);
    });

    let previousRevision = revisions[revisions.length - 2];

    if (!previousRevision) {
        toast.warn("No previous revision found");
        return;
    }

    // check the links and roll back any links
    let links = Object.fromEntries(
        Object
            .entries(item.Links)
            .filter(([key, value]) => {
                return value === item.RevisionID;
            }).map(([key, value]) => {
                return [`Links/${key}`, previousRevision.ID];
            })
    );

    form.setConfig({
        "title": "Rollback Revision",
        "description": `Rollback revision from ${item.Revision} to ${previousRevision.Revision}.`,
        "type": "update",
        "location": `${docCollection}/${item.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                [`Revisions/${item.RevisionID}`]: null,
                "RevisionID": previousRevision.ID,
                "Revision": previousRevision.Revision,
                ...links
            }
        }
    });
    form.toggle();
};

export const linkDocument = ({ form, pageItem, db, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");
    const docCollectionTail = parentTypeFor(variant, "Documents");
    const docRefs = refs(variant, "Documents");

    form.setConfig({
        "title": "Link Document",
        "description": `Link a Document to ${pageItem.Number}`,
        "type": "update",
        "location": `Database/${meta.ParentType}/${pageItem.ID}/${docCollectionTail}`,
        "fields": [{
            "label": "Document",
            "name": "_doc",
            "type": "dropdown",
            "dataLocation": docRefs,
            "labelKey": "Number",
            "idKey": "ID",
            "search": true
        }],
        preSubmitHook({ data }) {
            return {
                [data._doc]: true,
                "_doc": null,
                [meta.ParentType]: {
                    [pageItem.ID]: true
                }
            };
        },
        postSubmitHook({ liveInputData }) {
            db.set(`${docCollection}/${liveInputData._doc}/${meta.ParentType}/${pageItem.ID}`, true);
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const download = async ({ item, meta, pageItem, db, toast }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    
    // Use documentStoragePaths helper for consistent path generation
    const { outputDownloadPath, legacyOutputDownloadPath } = documentStoragePaths({
        variant,
        documentId: item.ID,
        docRevisionId: item.RevisionID,
        documentNumber: item.Number,
        revision: item.Revision
    });
    
    // Primary path: Database/{prefix}Documents/{docId}/Revisions/{docRevisionId}/{sanitizedNumber}_{revision}.docx
    // This matches the output from the server-side generateDocument function (PR #23/#26)
    try {
        const url = await db.download(outputDownloadPath);
        window.open(url, "_blank");
    } catch (err) {
        // Fallback to legacy flat path for documents generated before Revisions/ structure
        // Database/{prefix}Documents/{docId}/{sanitizedNumber}_{revision}.docx
        try {
            const url = await db.download(legacyOutputDownloadPath);
            window.open(url, "_blank");
        } catch (legacyErr) {
            toast.error("Document not found");
        }
    }
};

export const upload = async ({ item, meta, pageItem, db, toast }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    
    // Use documentStoragePaths helper for consistent path generation
    const { outputDownloadPath, legacyOutputDownloadPath } = documentStoragePaths({
        variant,
        documentId: item.ID,
        docRevisionId: item.RevisionID,
        documentNumber: item.Number,
        revision: item.Revision
    });
    
    // Primary path: Database/{prefix}Documents/{docId}/Revisions/{docRevisionId}/{sanitizedNumber}_{revision}.docx
    // This matches the output from the server-side generateDocument function (PR #23/#26)
    try {
        const url = await db.download(outputDownloadPath);
        window.open(url, "_blank");
    } catch (err) {
        // Fallback to legacy flat path for documents generated before Revisions/ structure
        // Database/{prefix}Documents/{docId}/{sanitizedNumber}_{revision}.docx
        try {
            const url = await db.download(legacyOutputDownloadPath);
            window.open(url, "_blank");
        } catch (legacyErr) {
            toast.error("Document not found");
        }
    }
};

export const switchParent = async ({ form, item, meta, pageItem, store, attachedItems }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");

    form.setConfig({
        "title": "Change Parent",
        "type": "update",
        "location": `${docCollection}/${item.ID}/`,
        "fields": [{
            "label": "New Parent",
            "type": "dropdown",
            "name": "ParentID",
            "dropdownData": makeDropdown(store, attachedItems[item.ParentType].items, "Number", "ID")
        }],
        preSubmitHook({ selectedDropdowns }) {
            return {
                "Parent": selectedDropdowns.ParentID.label,
                [`Links/${selectedDropdowns.ParentID.id}`]: item.RevisionID
            }
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const makePrototype = async ({ form, rowItem, pageItem, store, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");

    form.setConfig({
        "title": "Make this Document a Prototype",
        "type": "update",
        "location": `${docCollection}/${rowItem.ID}/`,
        "fields": [],
        preSubmitHook({ data }) {
            return {
                "Revision": "P1",
                "RevisionID": "---P1",
                "Signedoff": false,
                "Revisions": {
                    "---P1": {
                        "Date": (new Date()).toISOString(),
                        "Revision": "P1",
                        "RevisedBy": store.state.user.Basic.name,
                        "Reason": "Set to Prototype",
                        "ID": "---P1",
                        "Quality": revisionQuality(meta.ParentType === "Documents")
                    }
                },
                "Links": {
                    [pageItem.ID]: "---P1"
                }
            }
        }
    });

    form.toggle();
};

export const reviseDrawingToA = ({ form, rowItem, pageItem, store, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");

    form.setValues(rowItem);
    form.setConfig({
        "title": "Revise Prototype to Revision 1",
        "type": "update",
        "location": `${docCollection}/${rowItem.ID}`,
        "fields": [],
        preSubmitHook() {
            return {
                "Revision": "1",
                "RevisionID": "---R1",
                "Revisions/---R1": {
                    "Date": new Date().toISOString(),
                    "ID": "---R1",
                    "Quality": revisionQuality(meta.ParentType === "Documents"),
                    "RevisedBy": store.state.user.Basic.name,
                    "Reason": "From Prototype to Rev 1",
                    "Revision": "1"
                },
                [`Links/${pageItem.ID || rowItem.ParentID}`]: "---R1"
            }
        }
    });
    form.toggle()
};

export const unlinkDocument = ({ form, pageItem, rowItem, meta, db }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");
    const docCollectionTail = parentTypeFor(variant, "Documents");

    form.setConfig({
        "title": "Link a Document",
        "description": `Unlink a document to ${pageItem.Number}`,
        "type": "delete",
        "location": `Database/${meta.ParentType}/${pageItem.ID}/${docCollectionTail}/${rowItem.ID}`,
        "fields": [],
        postSubmitHook() {
            db.delete(`${docCollection}/${rowItem.ID}/${meta.ParentType}/${pageItem.ID}`);
        }
    });
    form.getDropdownData();
    form.toggle();
};

export const downloadBlankForm = async ({ rowItem, db, store }) => {
    const docType = await db.once(`Database/DocumentTypes/${rowItem.TypeID}`);
    let file = await db.download(`Database/DocumentTypes/${docType.ID}/Revisions/${docType.RevisionID}/File`);
    window.open(file, "__blank");
};

export const linkMiscellaneous = async ({ pageItem, db, form }) => {

};

export const changeLinkedRevision = ({ form, pageItem, rowItem, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? rowItem?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");

    form.setConfig({
        "title": "Change linked revision",
        "type": "update",
        "location": `${docCollection}/${rowItem.ID}/Links/`,
        "fields": [{
            "label": "Revision",
            "name": pageItem.ID,
            "type": "dropdown",
            dropdownData: Object.entries(rowItem.Revisions).map(([key, value]) => ({ id: key, label: value.Revision }))
        }]
    });
    form.getDropdownData();
    form.toggle();
};

export const updateUploadedStatus = async ({ form, item, meta, pageItem }) => {
    const variant = getVariantByParentType(meta?.ParentType ?? item?.ParentType ?? pageItem?.ParentType);
    const docCollection = collection(variant, "Documents");

    form.setValue({
        "UploadStatus": item.UploadStatus || "Not Uploaded"
    });

    form.setConfig({
        "title": "Update Upload Status",
        "type": "update",
        "location": `${docCollection}/${item.ID}`,
        "fields": [{
            "label": "Upload Status",
            "name": "UploadStatus",
            "type": "dropdown",
            "labelKey": "Name",
            "idKey": "Name",
            "dataLocation": "Database/UploadStatuses",

        }]
    });
    form.getDropdownData();
    form.toggle();
};

/**
 * Generates a document via the server-side Firebase callable.
 * 
 * The server-side Function:
 * - Renders the document using Docxtemplater with the current document revision data
 * - Saves to: Database/{prefix}Documents/{docId}/Revisions/{docRevisionId}/{sanitizedNumber}_{revision}.docx
 * - Returns the Storage path for authenticated download
 * 
 * Client responsibility:
 * - Call the generateDocument callable with documentId, documentRevisionId, and databaseURL
 * - Download the generated file from Storage using the returned path
 * - Save to user's browser
 * 
 * Gating (enforced in attachable.js show):
 * - Document Type must have a GeneratableFile template
 * - Approver must be Completed: true with a real By value (not "N/A" or empty)
 */
export const generateDocument = async ({ item, db, toast }) => {
    try {
        if (!item?.ID) {
            toast.error("Unable to generate document: document ID is missing.");
            return;
        }

        if (!item?.RevisionID) {
            toast.error("Unable to generate document: revision ID is missing.");
            return;
        }

        const databaseURL = appConfig?.firebaseConfig?.databaseURL;
        if (!databaseURL) {
            toast.error("Unable to generate document: database configuration is missing.");
            return;
        }

        toast.info("Generating document. This may take a moment...");

        const generate = firebase.app().functions().httpsCallable("generateDocument");
        const result = await generate({
            documentId: item.ID,
            documentRevisionId: item.RevisionID,
            databaseURL
        });

        if (!result.data?.success) {
            toast.error(result.data?.error || "Document generation failed.");
            return;
        }

        const { outputPath, filename } = result.data;

        if (!outputPath) {
            toast.error("Document generation failed: no output path returned.");
            return;
        }

        toast.info("Downloading generated document...");

        const url = await db.download(outputPath);
        window.open(url, "_blank");

        toast.success("Document generated successfully.");
    } catch (error) {
        console.error("[Documents][Generate] Callable failed", error);
        toast.error(error?.message || "Failed to generate document.");
    }
};

export const checkDocuments = ({ form, pageItem, meta }) => {
    const variant = getVariantByParentType(meta?.ParentType);
    const documentCollection = collection(variant, "Documents");
    form.setConfig({
        "title": "AI - Check Document",
        "type": "ignoreAutoAction",
        "successMessage": "Document check complete.",
        "description": "Upload the Document PDF to compare with the company handbooks. This can take a few minutes. Please note that this is a beta feature and may not be accurate and the results should be verified by a human.",
        "fields": [{
            "label": "PDF files",
            "name": "Files",
            "type": "file",
            "accept": "application/pdf"
        }],
        async postSubmitHook({ filesByName, db }) {
            const selected = filesByName?.Files || [];
            if (!selected.length) {
                throw new Error("Choose at least one PDF.");
            }
            const names = [];
            for (const file of selected) {
                if (!String(file.name || "").toLowerCase().endsWith(".pdf")) {
                    throw new Error("Only PDF files can be checked.");
                }
                await db.upload(`${documentCollection}/${pageItem.ID}/DocumentCheck`, file);
                names.push(file.name);
            }
            documentCheckLoadingFlags[pageItem.ID] = true;
            try {
                const check = firebase.app().functions().httpsCallable("checkProjectDocumentation", { timeout: 540000 });
                await check({
                    id: pageItem.ID,
                    collection: documentCollection,
                    databaseURL: appConfig?.firebaseConfig?.databaseURL,
                    files: names
                });
            } finally {
                documentCheckLoadingFlags[pageItem.ID] = false;
            }
        }
    });
    form.toggle();
};
